/**
 * SettingsAccessPage — three sections in one page:
 *
 *   1. Account     — change my own password (any user)
 *   2. Team        — list dashboard users; admin can create / deactivate /
 *                    reset password / change roles (admin-only)
 *   3. Restricted  — search creators / fans / brands by name and add them
 *                    to the platform-wide exclusion list (admin-only).
 *                    Excluded accounts are filtered out of every dashboard
 *                    metric AND every AI-chat query.
 *
 * All writes go to the local SQLite `auth.db` via existing /api/v1/auth/admin/*
 * endpoints. Postgres is read-only, untouched.
 */
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { tokensV2 as T } from "./tokens.v2";
import { LoadingShell, EmptyError } from "./PageState.v2";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4000/api/v1";

/* ───────── tiny fetch helper that always sends both auth headers ───────── */
async function apiFetch(path, { method = "GET", body = null, token = null } = {}) {
  const headers = { "Content-Type": "application/json" };
  const apiKey = process.env.REACT_APP_API_KEY;
  if (apiKey) headers["X-API-Key"] = apiKey;
  if (token)  headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.success) {
    const err = json.error;
    const msg = !err ? `Request failed (HTTP ${res.status})`
      : typeof err === "string" ? err
      : err.message ? err.message
      : JSON.stringify(err);
    const e = new Error(msg);
    e.status = res.status;
    throw e;
  }
  return json.data;
}

const ROLES = [
  { id: "ceo",     label: "CEO" },
  { id: "cmo",     label: "CMO" },
  { id: "cfo",     label: "CFO" },
  { id: "coo",     label: "COO" },
  { id: "cto",     label: "CTO" },
  { id: "mkt",     label: "Marketing" },
  { id: "sales",   label: "Sales" },
  { id: "fin",     label: "Finance" },
  { id: "ana",     label: "Analyst" },
  { id: "prod",    label: "Product" },
  { id: "sup",     label: "Support" },
  { id: "hr",      label: "HR" },
];

const ACCOUNT_TYPES = [
  { id: 2, label: "Fan" },
  { id: 3, label: "Creator" },
  { id: 4, label: "Brand" },
];

/* ─────────────────────────── Section: Account ─────────────────────────── */

function AccountSection({ authToken, authUser }) {
  const [current, setCurrent] = useState("");
  const [next, setNext]       = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy]       = useState(false);
  const [msg, setMsg]         = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (!current || !next) { setMsg({ kind: "error", text: "Enter your current and new password." }); return; }
    if (next.length < 8)   { setMsg({ kind: "error", text: "New password must be at least 8 characters." }); return; }
    if (next !== confirm)  { setMsg({ kind: "error", text: "New password and confirmation don't match." }); return; }
    setBusy(true);
    try {
      await apiFetch("/auth/change-password", {
        method: "POST", token: authToken,
        body: { currentPassword: current, newPassword: next },
      });
      setCurrent(""); setNext(""); setConfirm("");
      setMsg({ kind: "ok", text: "Password updated. Use it next time you sign in." });
    } catch (err) {
      setMsg({ kind: "error", text: err.message });
    } finally { setBusy(false); }
  };

  return (
    <div style={card()}>
      <SectionHeader
        title="Your account"
        sub={`Signed in as ${authUser?.name || authUser?.username || "—"} · ${(authUser?.role || authUser?.primary_role || "user").toUpperCase()}`}
      />
      <form onSubmit={submit} style={{ padding: 22, display: "grid", gap: 14, maxWidth: 480 }}>
        <Field label="Current password">
          <input type="password" value={current} onChange={e => setCurrent(e.target.value)} style={input()} autoComplete="current-password" />
        </Field>
        <Field label="New password" hint="At least 8 characters.">
          <input type="password" value={next} onChange={e => setNext(e.target.value)} style={input()} autoComplete="new-password" />
        </Field>
        <Field label="Confirm new password">
          <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} style={input()} autoComplete="new-password" />
        </Field>
        {msg && <Banner kind={msg.kind}>{msg.text}</Banner>}
        <div>
          <button type="submit" disabled={busy} style={primaryBtn({ disabled: busy })}>
            {busy ? "Updating…" : "Change password"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ──────────────────────────── Section: Team ─────────────────────────── */

function TeamSection({ authToken }) {
  const [users, setUsers]   = useState(null);
  const [error, setError]   = useState(null);
  const [busy, setBusy]     = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const reload = useCallback(async () => {
    setError(null);
    try {
      const data = await apiFetch("/auth/admin/users", { token: authToken });
      setUsers(data);
    } catch (err) { setError(err.message); }
  }, [authToken]);

  useEffect(() => { reload(); }, [reload]);

  const toggle = async (u) => {
    if (!window.confirm(`${u.is_active ? "Deactivate" : "Reactivate"} ${u.username}?`)) return;
    setBusy(true);
    try {
      await apiFetch("/auth/admin/toggle-user", { method: "POST", token: authToken, body: { user_id: u.id, is_active: !u.is_active } });
      await reload();
    } catch (err) { alert(err.message); }
    finally { setBusy(false); }
  };

  const resetPw = async (u) => {
    const pw = window.prompt(`Set a new password for "${u.username}". Tell them this and ask them to change it after sign-in.\n\n(Min 8 characters.)`);
    if (!pw) return;
    if (pw.length < 8) { alert("Password must be at least 8 characters."); return; }
    setBusy(true);
    try {
      await apiFetch("/auth/admin/reset-password", { method: "POST", token: authToken, body: { user_id: u.id, new_password: pw } });
      alert(`Password reset for ${u.username}.`);
    } catch (err) { alert(err.message); }
    finally { setBusy(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Permanently delete ${u.username}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await apiFetch(`/auth/admin/users/${u.id}`, { method: "DELETE", token: authToken });
      await reload();
    } catch (err) { alert(err.message); }
    finally { setBusy(false); }
  };

  if (!users && !error) return <LoadingShell tiles={2} />;
  if (error) return <EmptyError title="Couldn't load Team" error={error} onRetry={reload} />;

  return (
    <div style={card()}>
      <SectionHeader
        title="Team"
        sub={`${users.length} dashboard user${users.length === 1 ? "" : "s"}. Admin-only actions: add, deactivate, reset password, delete.`}
        action={
          <button type="button" onClick={() => setShowAdd(true)} style={primaryBtn()}>+ Add user</button>
        }
      />
      <div style={{ overflow: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: T.surface.canvas }}>
              {["User", "Role", "Status", "Actions"].map(h => (
                <th key={h} style={th()}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} style={{ borderTop: `1px solid ${T.border.subtle}` }}>
                <td style={td()}>
                  <div style={{ fontWeight: 700, color: T.text.primary }}>{u.name || u.username}</div>
                  <div style={{ fontSize: 11, color: T.text.muted, fontFamily: T.font.mono }}>{u.username}{u.is_superadmin ? " · superadmin" : ""}</div>
                </td>
                <td style={td()}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: T.text.secondary }}>{(u.primary_role || "—").toUpperCase()}</span>
                </td>
                <td style={td()}>
                  <span style={{
                    fontSize: 10, fontWeight: 800, padding: "3px 9px", borderRadius: 999,
                    background: u.is_active ? T.status.goodBg : T.status.badBg,
                    color: u.is_active ? T.status.good : T.status.bad,
                  }}>{u.is_active ? "ACTIVE" : "INACTIVE"}</span>
                </td>
                <td style={{ ...td(), whiteSpace: "nowrap" }}>
                  <button type="button" onClick={() => resetPw(u)} disabled={busy} style={tinyBtn()}>Reset password</button>
                  <button type="button" onClick={() => toggle(u)} disabled={busy || u.is_superadmin} style={tinyBtn({ marginLeft: 6 })}>
                    {u.is_active ? "Deactivate" : "Activate"}
                  </button>
                  <button type="button" onClick={() => remove(u)} disabled={busy || u.is_superadmin} style={tinyBtn({ marginLeft: 6, color: T.status.bad })}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showAdd && (
        <AddUserModal authToken={authToken} onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); reload(); }} />
      )}
    </div>
  );
}

function AddUserModal({ authToken, onClose, onCreated }) {
  const [u, setU] = useState({ username: "", name: "", primary_role: "ceo", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!u.username.trim() || !u.password) { setErr("Username and password are required."); return; }
    if (u.password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (u.password !== u.confirm) { setErr("Password and confirmation don't match."); return; }
    setBusy(true);
    try {
      await apiFetch("/auth/admin/users", {
        method: "POST", token: authToken,
        body: {
          username: u.username.trim().toLowerCase(),
          name: u.name.trim(),
          primary_role: u.primary_role,
          allowed_roles: [u.primary_role],
          password: u.password,
          is_superadmin: 0,
        },
      });
      onCreated();
    } catch (e2) { setErr(e2.message); }
    finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title="Add a new dashboard user">
      <form onSubmit={submit} style={{ display: "grid", gap: 12 }}>
        <Field label="Username" hint="Lowercase, no spaces (used to sign in).">
          <input value={u.username} onChange={e => setU({ ...u, username: e.target.value })} style={input()} autoFocus />
        </Field>
        <Field label="Display name" hint="Shown in the user pill.">
          <input value={u.name} onChange={e => setU({ ...u, name: e.target.value })} style={input()} />
        </Field>
        <Field label="Role">
          <select value={u.primary_role} onChange={e => setU({ ...u, primary_role: e.target.value })} style={input()}>
            {ROLES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        </Field>
        <Field label="Initial password" hint="At least 8 characters. Share with them privately.">
          <input type="password" value={u.password} onChange={e => setU({ ...u, password: e.target.value })} style={input()} autoComplete="new-password" />
        </Field>
        <Field label="Confirm password">
          <input type="password" value={u.confirm} onChange={e => setU({ ...u, confirm: e.target.value })} style={input()} autoComplete="new-password" />
        </Field>
        {err && <Banner kind="error">{err}</Banner>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 6 }}>
          <button type="button" onClick={onClose} style={secondaryBtn()}>Cancel</button>
          <button type="submit" disabled={busy} style={primaryBtn({ disabled: busy })}>
            {busy ? "Creating…" : "Create user"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ────────────────────── Section: Restricted Accounts ────────────────────── */

function RestrictedSection({ authToken }) {
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);
  const [reason, setReason] = useState("Internal team account");
  const [busy, setBusy] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  const reload = useCallback(async () => {
    setError(null);
    try {
      const data = await apiFetch("/auth/admin/restricted", { token: authToken });
      setList(data);
    } catch (err) { setError(err.message); }
  }, [authToken]);

  useEffect(() => { reload(); }, [reload]);

  const remove = async (id) => {
    if (!window.confirm("Remove this account from the exclusion list? Their data will start showing on the dashboard again.")) return;
    setBusy(true);
    try {
      await apiFetch(`/auth/admin/restricted/${id}`, { method: "DELETE", token: authToken });
      await reload();
    } catch (err) { alert(err.message); }
    finally { setBusy(false); }
  };

  if (!list && !error) return <LoadingShell tiles={2} />;
  if (error) return <EmptyError title="Couldn't load restricted accounts" error={error} onRetry={reload} />;

  return (
    <div style={card()}>
      <SectionHeader
        title="Restricted accounts"
        sub={`${list.length} account${list.length === 1 ? "" : "s"} excluded from every dashboard metric AND every AI-chat query. Use this for team members and test accounts you don't want in real numbers.`}
        action={
          <button type="button" onClick={() => setShowSearch(true)} style={primaryBtn()}>+ Search & add</button>
        }
      />
      <div style={{ overflow: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: T.surface.canvas }}>
              {["Account", "Type", "Reason", "Added", ""].map(h => (
                <th key={h} style={th()}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr><td colSpan={5} style={{ ...td(), textAlign: "center", color: T.text.muted, padding: "32px 14px" }}>
                No accounts excluded yet. Click <strong>+ Search & add</strong> to find creators / fans / brands by name.
              </td></tr>
            ) : list.map(r => (
              <tr key={r.id} style={{ borderTop: `1px solid ${T.border.subtle}` }}>
                <td style={td()}>
                  {/* API returns `name` (resolved live from Postgres) + `username`.
                      Older code read `account_name`, which the API never sends —
                      so every row showed "—". */}
                  <div style={{ fontWeight: 700, color: T.text.primary }}>{r.name || r.username || "—"}</div>
                  <div style={{ fontSize: 11, color: T.text.muted, fontFamily: T.font.mono }}>id {r.account_id}</div>
                </td>
                <td style={td()}>
                  <span style={typeBadge(r.account_type)}>{(r.account_type || "—").toUpperCase()}</span>
                </td>
                <td style={td()}><span style={{ fontSize: 12, color: T.text.secondary }}>{r.reason || "—"}</span></td>
                <td style={td()}>
                  <span style={{ fontSize: 11, color: T.text.muted, whiteSpace: "nowrap" }}>
                    {r.created_at ? new Date(r.created_at).toLocaleDateString("en-IN") : "—"}
                  </span>
                </td>
                <td style={td()}>
                  <button type="button" onClick={() => remove(r.id)} disabled={busy} style={tinyBtn({ color: T.status.bad })}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showSearch && (
        <SearchAddModal
          authToken={authToken}
          reason={reason} setReason={setReason}
          // Pass the current restricted list so the modal can hide rows that
          // are already excluded — they only show up under the table above
          // (where the user can Remove them).
          restrictedList={list || []}
          onClose={() => setShowSearch(false)}
          onAdded={() => { reload(); }}
        />
      )}
    </div>
  );
}

/** Compact INR formatter for the search/browse list (Cr / L / K). */
function formatINRCompact(n) {
  if (n == null) return "—";
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return "—";
  if (num >= 1e7) return "Rs " + (num / 1e7).toFixed(2) + "Cr";
  if (num >= 1e5) return "Rs " + (num / 1e5).toFixed(2) + "L";
  if (num >= 1e3) return "Rs " + (num / 1e3).toFixed(1) + "K";
  return "Rs " + Math.round(num).toLocaleString("en-IN");
}

/** Normalise the search-accounts API response: backend may return an array
 *  (legacy) or `{ results, meta }` (new). Always returns { results, meta }. */
function unpackSearchData(raw) {
  if (Array.isArray(raw)) return { results: raw, meta: null };
  if (raw && Array.isArray(raw.results)) return { results: raw.results, meta: raw.meta || null };
  return { results: [], meta: null };
}

function SearchAddModal({ authToken, reason, setReason, onClose, onAdded, restrictedList = [] }) {
  const [type, setType] = useState("creator");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [meta, setMeta] = useState(null);
  const [browseMode, setBrowseMode] = useState(true);
  const [busy, setBusy] = useState(false);
  const [searching, setSearching] = useState(false);
  const [err, setErr] = useState(null);

  // Build a fast lookup of the accounts that are ALREADY restricted, keyed by
  // `<type>:<account_id>`. Any row matching one of these is hidden from the
  // results below — those accounts only live in the table above.
  const restrictedKeys = useMemo(() => {
    const s = new Set();
    for (const r of restrictedList) {
      if (r?.account_id != null && r?.account_type) {
        s.add(`${String(r.account_type).toLowerCase()}:${r.account_id}`);
      }
    }
    return s;
  }, [restrictedList]);

  // Pull the top-spend (fans), top-earning (creators) or top-spend (brands)
  // list from the backend whenever the user opens the modal or switches the
  // type tab. The same endpoint serves both modes — no `q` param = browse.
  const fetchTop = useCallback(async (forType) => {
    setSearching(true); setErr(null); setBrowseMode(true);
    try {
      const data = await apiFetch(
        `/auth/admin/search-accounts?type=${encodeURIComponent(forType)}`,
        { token: authToken },
      );
      const { results: rs, meta: m } = unpackSearchData(data);
      setResults(rs); setMeta(m);
    } catch (e2) {
      setErr(e2.message); setResults([]); setMeta(null);
    } finally {
      setSearching(false);
    }
  }, [authToken]);

  // Live search — fires automatically as the user types. The backend already
  // does prefix/substring matching on first_name / last_name / username / id,
  // so a single character is enough to start narrowing the list.
  //
  // Empty string → reset back to the top-spenders browse list. Non-empty →
  // debounced /search-accounts?q= call with the latest term. Each new keystroke
  // bumps a request id so a slow earlier response can't overwrite a faster
  // later one (race-condition guard).
  const reqIdRef = useRef(0);
  useEffect(() => {
    const term = q.trim();
    if (!term) { fetchTop(type); return; }
    const myReq = ++reqIdRef.current;
    setSearching(true); setErr(null); setBrowseMode(false);
    const t = setTimeout(async () => {
      try {
        const data = await apiFetch(
          `/auth/admin/search-accounts?type=${encodeURIComponent(type)}&q=${encodeURIComponent(term)}`,
          { token: authToken },
        );
        if (myReq !== reqIdRef.current) return; // stale, newer keystroke in flight
        const { results: rs, meta: m } = unpackSearchData(data);
        setResults(rs); setMeta(m);
      } catch (e2) {
        if (myReq !== reqIdRef.current) return;
        setErr(e2.message); setResults([]); setMeta(null);
      } finally {
        if (myReq === reqIdRef.current) setSearching(false);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [q, type, authToken, fetchTop]);

  // Initial open + every type switch → refresh the browse list (only when
  // the box is empty; otherwise the q-effect above is already driving fetches).
  useEffect(() => {
    if (!q.trim()) fetchTop(type);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [type, fetchTop]);

  const doSearch = (e) => { if (e) e.preventDefault(); /* enter still works; live effect already fetched */ };

  const clearSearch = () => { setQ(""); /* q-effect handles the rest */ };

  // Top up the visible suggestion list — re-runs the SAME query the user is
  // looking at (browse OR search) so the modal always has a fresh page of
  // candidates. Called after every successful add. Bumps reqIdRef so it
  // can't race with an in-flight q-effect fetch.
  const refetchCurrent = useCallback(async () => {
    const term = q.trim();
    const url = term
      ? `/auth/admin/search-accounts?type=${encodeURIComponent(type)}&q=${encodeURIComponent(term)}`
      : `/auth/admin/search-accounts?type=${encodeURIComponent(type)}`;
    const myReq = ++reqIdRef.current;
    try {
      const data = await apiFetch(url, { token: authToken });
      if (myReq !== reqIdRef.current) return;
      const { results: rs, meta: m } = unpackSearchData(data);
      setResults(rs); setMeta(m);
    } catch (_) { /* leave optimistic state alone — the user already got feedback */ }
  }, [authToken, q, type]);

  const add = async (acc) => {
    setBusy(true); setErr(null);
    try {
      await apiFetch("/auth/admin/restricted", {
        method: "POST", token: authToken,
        body: {
          account_id: acc.id,
          account_type: type,
          // Backend POST reads `name` + `username` (not `account_name`) — send
          // those so the stored fallback is correct. (Display also resolves the
          // name live from Postgres, so this is belt-and-suspenders.)
          name: acc.name || acc.username || `id ${acc.id}`,
          username: acc.username || "",
          reason: reason || "Internal team account",
        },
      });
      // Optimistic update — drop the just-added row from the local list so the
      // user sees instant feedback before the refetch returns.
      setResults(rs => rs.filter(r => (r.account_id ?? r.id) !== acc.id));
      // Tell the parent to refresh restrictedList (drives the table + the
      // restrictedKeys filter inside this modal).
      onAdded();
      // Top up the suggestion list immediately. Backend NOT-IN exclusion now
      // includes the just-restricted ID, so the response is the next page of
      // candidates — list never goes empty as long as there are more accounts
      // on the platform.
      refetchCurrent();
    } catch (e2) {
      // Backend likely returned 409 if already restricted — surface friendly
      setErr(/already/i.test(e2.message) ? "Already restricted." : e2.message);
    } finally { setBusy(false); }
  };

  // Stale-backend fallback: if every row the backend just returned is already
  // in the restricted set, the backend either has stale code or stale cache —
  // ask for a fresh page once so the modal doesn't get stuck on the empty
  // "all already restricted" message. Bounded to 2 attempts so we never loop.
  const staleRetryRef = useRef(0);

  // Hide rows that are already in the restricted list. Computed at render
  // time so any parent-side reload immediately reflects in the modal.
  const visibleResults = useMemo(() => {
    const filtered = results.filter(r => {
      const accId = r.account_id ?? r.id;
      return !restrictedKeys.has(`${type}:${accId}`);
    });
    // In browse mode the backend already ranks by spend/earnings — keep that.
    const term = q.trim().toLowerCase();
    if (!term) return filtered;
    // In search mode, the backend uses LIKE '%term%' which means it returns
    // substring matches anywhere — including unrelated rows whose username
    // happens to contain the term mid-word (e.g. "tanu_MOdern_beauty" matches
    // "mo"). That's noise from the CEO's perspective: typing "mo" should
    // surface Mohit / Monu / Mohammed first, not Tanu and Sourav.
    //
    // Rank tiers (lower = better):
    //   0 — name OR username STARTS with the term ("Mohit", "Monu")
    //   1 — any word in the display name starts with the term ("Ali Mohan" → "Mohan")
    //   2 — username has a word-boundary start matching ("john_mohan" → token "mohan")
    //   3 — everything else (raw substring fallback)
    const tierOf = (r) => {
      const name = String(r.name || "").toLowerCase();
      const uname = String(r.username || "").toLowerCase().replace(/^@/, "");
      if (name.startsWith(term) || uname.startsWith(term)) return 0;
      const nameWords = name.split(/\s+/).filter(Boolean);
      if (nameWords.some(w => w.startsWith(term))) return 1;
      const unameTokens = uname.split(/[._\-\s]+/).filter(Boolean);
      if (unameTokens.some(w => w.startsWith(term))) return 2;
      return 3;
    };
    return filtered
      .map(r => ({ r, t: tierOf(r), nlen: String(r.name || r.username || "").length }))
      // Stable secondary sort by name length so "Mo" beats "Mohammad Hammad" inside the same tier.
      .sort((a, b) => a.t - b.t || a.nlen - b.nlen)
      .map(x => x.r);
  }, [results, restrictedKeys, type, q]);
  const hiddenCount = results.length - visibleResults.length;

  // If backend returned a full page but the frontend filter knocked them all
  // out, the backend cache must be stale → refetch once to pull a fresh page.
  // Reset the retry counter as soon as we see at least one fresh row.
  useEffect(() => {
    if (visibleResults.length > 0) {
      staleRetryRef.current = 0;
      return;
    }
    if (results.length > 0 && hiddenCount === results.length && !searching && staleRetryRef.current < 2) {
      staleRetryRef.current += 1;
      refetchCurrent();
    }
  }, [results.length, visibleResults.length, hiddenCount, searching, refetchCurrent]);

  return (
    <Modal onClose={onClose} title="Search & add a team account" wide>
      <div style={{ display: "grid", gap: 12 }}>
        <div style={{ display: "flex", gap: 8 }}>
          {ACCOUNT_TYPES.map(t => {
            const k = t.label.toLowerCase();
            const active = type === k;
            return (
              <button key={k} type="button" onClick={() => { setType(k); setResults([]); }}
                style={{
                  padding: "7px 14px", borderRadius: 999,
                  border: `1px solid ${active ? T.brand.base : T.border.subtle}`,
                  background: active ? T.brand.soft : T.surface.canvas,
                  color: active ? T.brand.base : T.text.primary,
                  fontSize: 12, fontWeight: 700, cursor: "pointer",
                }}>{t.label}</button>
            );
          })}
        </div>
        <form onSubmit={doSearch} style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1, position: "relative" }}>
            <input value={q} onChange={e => setQ(e.target.value)}
              placeholder={`Type a name or username — suggestions appear as you type…`}
              style={{ ...input(), width: "100%", paddingRight: q ? 64 : 36 }} autoFocus />
            {/* Live search-status indicator inside the input */}
            {searching && !browseMode && (
              <span aria-hidden style={{
                position: "absolute", right: q ? 38 : 12, top: "50%",
                width: 14, height: 14, borderRadius: "50%",
                border: `2px solid ${T.brand.soft}`, borderTopColor: T.brand.base,
                animation: "fcSpinSearch 0.8s linear infinite",
              }} />
            )}
            {q && (
              <button type="button" onClick={clearSearch} title="Clear search"
                style={{
                  position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                  width: 24, height: 24, borderRadius: "50%",
                  background: T.surface.canvas, border: `1px solid ${T.border.subtle}`,
                  color: T.text.muted, cursor: "pointer",
                  fontSize: 14, lineHeight: 1, fontFamily: "inherit",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>×</button>
            )}
          </div>
        </form>
        <style>{`@keyframes fcSpinSearch { from { transform: translateY(-50%) rotate(0deg); } to { transform: translateY(-50%) rotate(360deg); } }`}</style>
        <Field label="Reason for excluding (optional)">
          <input value={reason} onChange={e => setReason(e.target.value)} style={input()} />
        </Field>

        {err && <Banner kind="error">{err}</Banner>}

        {/* Browse-mode banner so the CEO immediately knows these are the
            real top-spending fans / top-earning creators / top-spending brands —
            no need to remember names just to find them. */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
          textTransform: "uppercase", color: T.text.muted,
          padding: "0 4px",
        }}>
          <span>
            {browseMode
              ? type === "fan"     ? "Top fans by all-time spend"
              : type === "creator" ? "Top creators by all-time earnings"
              : "Top brands by all-time spend"
              : `Search results for "${q.trim()}"`}
          </span>
          <span style={{ fontWeight: 600, textTransform: "none", letterSpacing: 0 }}>
            {visibleResults.length > 0
              ? `${visibleResults.length} shown${hiddenCount > 0 ? ` · ${hiddenCount} already restricted` : ""}`
              : ""}
          </span>
        </div>

        <div style={{ maxHeight: 320, overflow: "auto", border: `1px solid ${T.border.subtle}`, borderRadius: 10 }}>
          {visibleResults.length === 0 ? (
            <div style={{ padding: 24, color: T.text.muted, fontSize: 12, textAlign: "center", lineHeight: 1.6 }}>
              {searching ? "Loading…" : (() => {
                // Use server-side meta when available so the empty-state copy
                // is accurate. Three distinct cases:
                //  (a) platform truly has no more candidates of this type
                //  (b) all rows the backend returned are already restricted
                //      AND the platform has more rows we haven't loaded — this
                //      means backend exclusion is broken (stale code/cache)
                //  (c) no search match
                const totalForType  = meta?.totalForType;
                const excludedCount = meta?.totalForType != null ? (meta?.excludedCount ?? 0) : null;
                const remaining     = (totalForType != null && excludedCount != null)
                  ? Math.max(0, totalForType - excludedCount)
                  : null;

                if (!browseMode) {
                  return <>No <strong>{type}s</strong> matched <strong>"{q.trim()}"</strong>.<br />Try another spelling, or clear the search to see top spenders.</>;
                }
                // Browse mode — every row was filtered out:
                if (hiddenCount > 0 && remaining != null && remaining > 0) {
                  // Backend handed back already-restricted rows even though
                  // there are unrestricted ones left. That's a backend issue.
                  return (
                    <>
                      Backend returned {hiddenCount} already-restricted {type}{hiddenCount === 1 ? "" : "s"} —
                      but the platform has <strong>{remaining}</strong> {type}{remaining === 1 ? "" : "s"} not yet restricted.<br />
                      Restart the backend so the new exclusion code takes effect.
                    </>
                  );
                }
                if (remaining === 0 && totalForType > 0) {
                  return (
                    <>
                      You've restricted every <strong>{type}</strong> on the platform
                      ({totalForType} total).<br />
                      Remove someone from the table above to free up a slot, or switch to another type.
                    </>
                  );
                }
                if (totalForType === 0) {
                  return <>No <strong>{type}s</strong> exist on the platform yet.</>;
                }
                // Fallback (no meta available — old backend)
                return hiddenCount > 0
                  ? <>All {hiddenCount} match{hiddenCount === 1 ? "" : "es"} are already restricted. Manage them from the table above.</>
                  : <>No <strong>{type}s</strong> to show right now.</>;
              })()}
            </div>
          ) : visibleResults.map(r => {
            const accId = r.account_id ?? r.id;
            const totalValue = Number(r.total_value);
            const showAmount = browseMode && Number.isFinite(totalValue) && totalValue > 0;
            return (
              <div key={`${type}-${accId}`} style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "10px 14px",
                borderBottom: `1px solid ${T.border.subtle}`,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: T.text.primary, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {r.name || r.username || `id ${accId}`}
                  </div>
                  <div style={{ fontSize: 11, color: T.text.muted, fontFamily: T.font.mono }}>
                    id {accId}{r.username ? ` · ${r.username}` : ""}
                  </div>
                </div>
                {showAmount && (
                  <div style={{
                    fontSize: 12, fontWeight: 800, color: T.text.primary,
                    fontVariantNumeric: "tabular-nums", minWidth: 80, textAlign: "right",
                  }}>
                    {formatINRCompact(totalValue)}
                  </div>
                )}
                <button type="button" onClick={() => add({ ...r, id: accId })} disabled={busy} style={primaryBtn({ small: true, disabled: busy })}>
                  Restrict
                </button>
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button type="button" onClick={onClose} style={secondaryBtn()}>Done</button>
        </div>
      </div>
    </Modal>
  );
}

/* ─────────────────────────── Shared bits ─────────────────────────── */

function SectionHeader({ title, sub, action }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 16, padding: "18px 22px", borderBottom: `1px solid ${T.border.subtle}` }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: T.text.primary, letterSpacing: "-0.01em" }}>{title}</div>
        {sub && <div style={{ fontSize: 12, color: T.text.muted, marginTop: 4, lineHeight: 1.5, maxWidth: 720 }}>{sub}</div>}
      </div>
      {action}
    </div>
  );
}
function Field({ label, hint, children }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: T.text.secondary, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
      {children}
      {hint && <div style={{ fontSize: 11, color: T.text.muted, marginTop: 4 }}>{hint}</div>}
    </label>
  );
}
function Banner({ kind, children }) {
  const isOk = kind === "ok";
  return (
    <div style={{
      padding: "9px 13px", borderRadius: 10, fontSize: 12, fontWeight: 600,
      background: isOk ? T.status.goodBg : T.status.badBg,
      color: isOk ? T.status.good : T.status.bad,
      border: `1px solid ${(isOk ? T.status.good : T.status.bad)}33`,
    }}>{children}</div>
  );
}
function Modal({ onClose, title, wide, children }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);
  return (
    <div onClick={onClose} role="presentation" style={{
      position: "fixed", inset: 0, zIndex: 100,
      background: "rgba(31,42,63,0.5)",
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      padding: "60px 20px", overflowY: "auto",
    }}>
      <div onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={title}
        style={{
          width: "100%", maxWidth: wide ? 640 : 460,
          background: T.surface.card, border: `1px solid ${T.border.subtle}`,
          borderRadius: 16, boxShadow: "0 24px 60px rgba(31,42,63,0.20)",
          fontFamily: T.font.sans,
        }}>
        <div style={{ padding: "16px 22px", borderBottom: `1px solid ${T.border.subtle}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: T.text.primary }}>{title}</div>
          <button type="button" onClick={onClose} style={{ width: 30, height: 30, borderRadius: 8, background: T.surface.canvas, border: `1px solid ${T.border.subtle}`, color: T.text.muted, cursor: "pointer", fontSize: 16 }}>×</button>
        </div>
        <div style={{ padding: 22 }}>{children}</div>
      </div>
    </div>
  );
}

/* ─────────────────────────── Style helpers ─────────────────────────── */
function card() { return { background: T.surface.card, border: `1px solid ${T.border.subtle}`, borderRadius: 14, marginBottom: 16, overflow: "hidden", boxShadow: "0 4px 14px rgba(31,42,63,0.04)" }; }
function input() { return { width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${T.border.default}`, fontSize: 13, fontFamily: "inherit", color: T.text.primary, background: T.surface.canvas, outline: "none", boxSizing: "border-box" }; }
function primaryBtn({ disabled = false, small = false } = {}) {
  return {
    padding: small ? "6px 14px" : "10px 18px", borderRadius: 10, border: "none",
    background: disabled ? T.border.subtle : `linear-gradient(135deg, ${T.brand.base} 0%, ${T.brand.deep} 100%)`,
    color: disabled ? T.text.muted : "#FFFFFF",
    fontSize: small ? 11 : 13, fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer", fontFamily: "inherit",
    boxShadow: disabled ? "none" : `0 4px 12px ${T.brand.soft}`,
    whiteSpace: "nowrap",
  };
}
function secondaryBtn() {
  return { padding: "10px 18px", borderRadius: 10, border: `1px solid ${T.border.subtle}`, background: T.surface.card, color: T.text.primary, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" };
}
function tinyBtn(extra = {}) {
  return {
    padding: "5px 10px", borderRadius: 8,
    border: `1px solid ${T.border.subtle}`,
    background: T.surface.canvas, color: T.text.secondary,
    fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
    ...extra,
  };
}
function th() { return { textAlign: "left", padding: "10px 14px", fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: T.text.muted }; }
function td() { return { padding: "10px 14px", fontSize: 13, color: T.text.primary, verticalAlign: "middle" }; }
function typeBadge(t) {
  const map = { creator: T.chart.violet, fan: T.brand.base, brand: T.chart.pink };
  const c = map[String(t || "").toLowerCase()] || T.text.muted;
  return { fontSize: 10, fontWeight: 800, padding: "3px 9px", borderRadius: 999, background: `${c}15`, color: c, letterSpacing: "0.04em" };
}

/* ─────────────────────────── Page shell ─────────────────────────── */

export function SettingsAccessPage({ authToken, authUser }) {
  const [tab, setTab] = useState("account");
  const isAdmin = !!(authUser && (authUser.is_superadmin || /superadmin|admin|ceo/i.test(authUser.role || authUser.primary_role || "")));

  if (!authToken) {
    return <EmptyError title="Sign in to access settings" error="No session" onRetry={null} />;
  }

  const tabs = [
    { id: "account",    label: "Account",            adminOnly: false },
    { id: "team",       label: "Team",               adminOnly: true },
    { id: "restricted", label: "Restricted accounts", adminOnly: true },
  ].filter(t => !t.adminOnly || isAdmin);

  return (
    <>
      <div style={{ marginBottom: 16, padding: "0 4px" }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: T.text.primary, letterSpacing: "-0.01em" }}>Settings &amp; access</div>
        <div style={{ fontSize: 12, color: T.text.muted, marginTop: 4 }}>
          Manage your password, team users, and accounts you want excluded from every dashboard metric.
        </div>
      </div>

      <div style={{
        display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap",
        borderBottom: `1px solid ${T.border.subtle}`, paddingBottom: 0,
      }}>
        {tabs.map(t => {
          const active = tab === t.id;
          return (
            <button key={t.id} type="button" onClick={() => setTab(t.id)}
              style={{
                padding: "10px 14px", border: "none",
                background: "transparent",
                color: active ? T.brand.base : T.text.secondary,
                fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                borderBottom: `2px solid ${active ? T.brand.base : "transparent"}`,
                marginBottom: -1,
              }}>{t.label}</button>
          );
        })}
      </div>

      {tab === "account"    && <AccountSection authToken={authToken} authUser={authUser} />}
      {tab === "team"       && isAdmin && <TeamSection authToken={authToken} />}
      {tab === "restricted" && isAdmin && <RestrictedSection authToken={authToken} />}
      {(tab === "team" || tab === "restricted") && !isAdmin && (
        <EmptyError title="Admin access required" error="This section is only available to admin / superadmin users." onRetry={null} />
      )}
    </>
  );
}
